import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { CancellationToken, CancellationTokenSource } from 'vscode/vscode/vs/base/common/cancellation';
import { Emitter, Event } from 'vscode/vscode/vs/base/common/event';
import { Lazy } from 'vscode/vscode/vs/base/common/lazy';
import { Disposable, MutableDisposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { GeneratingPhrase, showChatView } from 'vscode/vscode/vs/workbench/contrib/chat/browser/chat';
import { IChatAccessibilityService, IChatCodeBlockContextProviderService } from 'vscode/vscode/vs/workbench/contrib/chat/browser/chat.service';
import { ChatAgentLocation } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatAgents';
import { IChatAgentService } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatAgents.service';
import { InteractiveSessionVoteDirection } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatService';
import { IChatService } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatService.service';
import { isDetachedTerminalInstance } from 'vscode/vscode/vs/workbench/contrib/terminal/browser/terminal';
import { ITerminalService } from 'vscode/vscode/vs/workbench/contrib/terminal/browser/terminal.service';
import { TerminalChatWidget } from './terminalChatWidget.js';
import { MarkdownString } from 'vscode/vscode/vs/base/common/htmlContent';
import { getHistoryEntriesFromModel } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatModel';
import { TerminalChatContextKeys } from './terminalChat.js';
import { IViewsService } from 'vscode/vscode/vs/workbench/services/views/common/viewsService.service';

var TerminalChatController_1;
let TerminalChatController = class TerminalChatController extends Disposable {
    static { TerminalChatController_1 = this; }
    static { this.ID = 'terminal.chat'; }
    static get(instance) {
        return instance.getContribution(TerminalChatController_1.ID);
    }
    get chatWidget() { return this._chatWidget?.value; }
    get lastResponseContent() {
        return this._lastResponseContent;
    }
    constructor(_instance, processManager, widgetManager, _terminalService, _instantiationService, _chatAgentService, _contextKeyService, _chatAccessibilityService, _chatService, _chatCodeBlockContextProviderService, _viewsService) {
        super();
        this._instance = _instance;
        this._terminalService = _terminalService;
        this._instantiationService = _instantiationService;
        this._chatAgentService = _chatAgentService;
        this._contextKeyService = _contextKeyService;
        this._chatAccessibilityService = _chatAccessibilityService;
        this._chatService = _chatService;
        this._chatCodeBlockContextProviderService = _chatCodeBlockContextProviderService;
        this._viewsService = _viewsService;
        this._messages = this._store.add(( new Emitter()));
        this.onDidAcceptInput = Event.filter(this._messages.event, m => m === 32 , this._store);
        this.onDidCancelInput = Event.filter(this._messages.event, m => m === 16  || m === 2 , this._store);
        this._terminalAgentName = 'terminal';
        this._model = this._register(( new MutableDisposable()));
        this._forcedPlaceholder = undefined;
        this._requestActiveContextKey = TerminalChatContextKeys.requestActive.bindTo(this._contextKeyService);
        this._terminalAgentRegisteredContextKey = TerminalChatContextKeys.agentRegistered.bindTo(this._contextKeyService);
        this._responseContainsCodeBlockContextKey = TerminalChatContextKeys.responseContainsCodeBlock.bindTo(this._contextKeyService);
        this._responseContainsMulitpleCodeBlocksContextKey = TerminalChatContextKeys.responseContainsMultipleCodeBlocks.bindTo(this._contextKeyService);
        this._responseSupportsIssueReportingContextKey = TerminalChatContextKeys.responseSupportsIssueReporting.bindTo(this._contextKeyService);
        this._sessionResponseVoteContextKey = TerminalChatContextKeys.sessionResponseVote.bindTo(this._contextKeyService);
        if (!this.initTerminalAgent()) {
            this._register(this._chatAgentService.onDidChangeAgents(() => this.initTerminalAgent()));
        }
        this._register(this._chatCodeBlockContextProviderService.registerProvider({
            getCodeBlockContext: (editor) => {
                if (!editor || !this._chatWidget?.hasValue || !this.hasFocus()) {
                    return;
                }
                return {
                    element: editor,
                    code: editor.getValue(),
                    codeBlockIndex: 0,
                    languageId: editor.getModel().getLanguageId()
                };
            }
        }, 'terminal'));
        this._register(this._chatService.onDidPerformUserAction(e => {
            if (this._chatWidget?.rawValue?.inlineChatWidget.usesDefaultChatModel
                && e.sessionId === this._chatWidget?.rawValue?.inlineChatWidget.getChatModel().sessionId) {
                if (e.action.kind === 'bug') {
                    this.acceptFeedback(undefined);
                }
                else if (e.action.kind === 'vote') {
                    this.acceptFeedback(e.action.direction === InteractiveSessionVoteDirection.Up);
                }
            }
        }));
    }
    initTerminalAgent() {
        const terminalAgent = this._chatAgentService.getAgentsByName(this._terminalAgentName)[0];
        if (terminalAgent) {
            this._terminalAgentId = terminalAgent.id;
            this._terminalAgentRegisteredContextKey.set(true);
            return true;
        }
        return false;
    }
    xtermReady(xterm) {
        this._chatWidget = ( new Lazy(() => {
            const chatWidget = this._register(this._instantiationService.createInstance(TerminalChatWidget, this._instance.domElement, this._instance, xterm));
            this._register(chatWidget.focusTracker.onDidFocus(() => {
                TerminalChatController_1.activeChatWidget = this;
                if (!isDetachedTerminalInstance(this._instance)) {
                    this._terminalService.setActiveInstance(this._instance);
                }
            }));
            this._register(chatWidget.focusTracker.onDidBlur(() => {
                TerminalChatController_1.activeChatWidget = undefined;
                this._instance.resetScrollbarVisibility();
            }));
            if (!this._instance.domElement) {
                throw ( new Error('FindWidget expected terminal DOM to be initialized'));
            }
            return chatWidget;
        }));
    }
    acceptFeedback(helpful) {
        const model = this._model.value;
        if (!this._currentRequest || !model) {
            return;
        }
        let action;
        if (helpful === undefined) {
            action = { kind: 'bug' };
        }
        else {
            this._sessionResponseVoteContextKey.set(helpful ? 'up' : 'down');
            action = { kind: 'vote', direction: helpful ? InteractiveSessionVoteDirection.Up : InteractiveSessionVoteDirection.Down };
        }
        for (const request of model.getRequests()) {
            if (request.response?.response.value || request.response?.result) {
                this._chatService.notifyUserAction({
                    sessionId: request.session.sessionId,
                    requestId: request.id,
                    agentId: request.response?.agent?.id,
                    result: request.response?.result,
                    action
                });
            }
        }
        this._chatWidget?.value.inlineChatWidget.updateStatus('Thank you for your feedback!', { resetAfter: 1250 });
    }
    cancel() {
        if (this._currentRequest) {
            this._model.value?.cancelRequest(this._currentRequest);
        }
        this._requestActiveContextKey.set(false);
        this._chatWidget?.value.inlineChatWidget.updateProgress(false);
        this._chatWidget?.value.inlineChatWidget.updateInfo('');
        this._chatWidget?.value.inlineChatWidget.updateToolbar(true);
    }
    _updatePlaceholder() {
        const inlineChatWidget = this._chatWidget?.value.inlineChatWidget;
        if (inlineChatWidget) {
            inlineChatWidget.placeholder = this._getPlaceholderText();
        }
    }
    _getPlaceholderText() {
        return this._forcedPlaceholder ?? '';
    }
    setPlaceholder(text) {
        this._forcedPlaceholder = text;
        this._updatePlaceholder();
    }
    resetPlaceholder() {
        this._forcedPlaceholder = undefined;
        this._updatePlaceholder();
    }
    clear() {
        if (this._currentRequest) {
            this._model.value?.cancelRequest(this._currentRequest);
        }
        this._model.clear();
        this._chatWidget?.rawValue?.hide();
        this._chatWidget?.rawValue?.setValue(undefined);
        this._responseContainsCodeBlockContextKey.reset();
        this._sessionResponseVoteContextKey.reset();
        this._requestActiveContextKey.reset();
    }
    async acceptInput() {
        if (!this._model.value) {
            this._model.value = this._chatService.startSession(ChatAgentLocation.Terminal, CancellationToken.None);
            if (!this._model.value) {
                throw ( new Error('Could not start chat session'));
            }
        }
        this._messages.fire(32 );
        const model = this._model.value;
        this._lastInput = this._chatWidget?.value?.input();
        if (!this._lastInput) {
            return;
        }
        const accessibilityRequestId = this._chatAccessibilityService.acceptRequest();
        this._requestActiveContextKey.set(true);
        const cancellationToken = ( new CancellationTokenSource()).token;
        let responseContent = '';
        const progressCallback = (progress) => {
            if (cancellationToken.isCancellationRequested) {
                return;
            }
            if (progress.kind === 'markdownContent') {
                responseContent += progress.content.value;
            }
            if (this._currentRequest) {
                model.acceptResponseProgress(this._currentRequest, progress);
            }
        };
        await model.waitForInitialization();
        this._chatWidget?.value.addToHistory(this._lastInput);
        const request = {
            text: this._lastInput,
            parts: []
        };
        const requestVarData = {
            variables: []
        };
        this._currentRequest = model.addRequest(request, requestVarData, 0);
        const requestProps = {
            sessionId: model.sessionId,
            requestId: this._currentRequest.id,
            agentId: this._terminalAgentId,
            message: this._lastInput,
            variables: { variables: [] },
            location: ChatAgentLocation.Terminal
        };
        try {
            const task = this._chatAgentService.invokeAgent(this._terminalAgentId, requestProps, progressCallback, getHistoryEntriesFromModel(model, this._terminalAgentId), cancellationToken);
            this._chatWidget?.value.inlineChatWidget.updateChatMessage(undefined);
            this._chatWidget?.value.inlineChatWidget.updateFollowUps(undefined);
            this._chatWidget?.value.inlineChatWidget.updateProgress(true);
            this._chatWidget?.value.inlineChatWidget.updateInfo(GeneratingPhrase + '\u2026');
            await task;
        }
        catch (e) {
        }
        finally {
            this._requestActiveContextKey.set(false);
            this._chatWidget?.value.inlineChatWidget.updateProgress(false);
            this._chatWidget?.value.inlineChatWidget.updateInfo('');
            this._chatWidget?.value.inlineChatWidget.updateToolbar(true);
            if (this._currentRequest) {
                model.completeResponse(this._currentRequest);
            }
            this._lastResponseContent = responseContent;
            if (this._currentRequest) {
                this._chatAccessibilityService.acceptResponse(responseContent, accessibilityRequestId);
                const containsCode = responseContent.includes('```');
                this._chatWidget?.value.inlineChatWidget.updateChatMessage({ message: ( new MarkdownString(responseContent)), requestId: this._currentRequest.id }, false, containsCode);
                const firstCodeBlock = await this.chatWidget?.inlineChatWidget.getCodeBlockInfo(0);
                const secondCodeBlock = await this.chatWidget?.inlineChatWidget.getCodeBlockInfo(1);
                this._responseContainsCodeBlockContextKey.set(!!firstCodeBlock);
                this._responseContainsMulitpleCodeBlocksContextKey.set(!!secondCodeBlock);
                this._chatWidget?.value.inlineChatWidget.updateToolbar(true);
            }
            const supportIssueReporting = this._currentRequest?.response?.agent?.metadata?.supportIssueReporting;
            if (supportIssueReporting !== undefined) {
                this._responseSupportsIssueReportingContextKey.set(supportIssueReporting);
            }
        }
    }
    updateInput(text, selectAll = true) {
        const widget = this._chatWidget?.value.inlineChatWidget;
        if (widget) {
            widget.value = text;
            if (selectAll) {
                widget.selectAll();
            }
        }
    }
    getInput() {
        return this._chatWidget?.value.input() ?? '';
    }
    focus() {
        this._chatWidget?.value.focus();
    }
    hasFocus() {
        return !!this._chatWidget?.rawValue?.hasFocus() ?? false;
    }
    async acceptCommand(shouldExecute) {
        const code = await this.chatWidget?.inlineChatWidget.getCodeBlockInfo(0);
        if (!code) {
            return;
        }
        this._chatWidget?.value.acceptCommand(code.textEditorModel.getValue(), shouldExecute);
    }
    reveal() {
        this._chatWidget?.value.reveal();
    }
    async viewInChat() {
        const widget = await showChatView(this._viewsService);
        const request = this._currentRequest;
        if (!widget || !request?.response) {
            return;
        }
        const message = [];
        for (const item of request.response.response.value) {
            if (item.kind === 'textEditGroup') {
                for (const group of item.edits) {
                    message.push({
                        kind: 'textEdit',
                        edits: group,
                        uri: item.uri
                    });
                }
            }
            else {
                message.push(item);
            }
        }
        this._chatService.addCompleteRequest(widget.viewModel.sessionId,
        `@${this._terminalAgentName} ${request.message.text}`, request.variableData, request.attempt, {
            message,
            result: request.response.result,
            followups: request.response.followups
        });
        widget.focusLastMessage();
        this._chatWidget?.rawValue?.hide();
    }
    dispose() {
        if (this._currentRequest) {
            this._model.value?.cancelRequest(this._currentRequest);
        }
        super.dispose();
        this.clear();
    }
};
TerminalChatController = TerminalChatController_1 = ( __decorate([
    ( __param(3, ITerminalService)),
    ( __param(4, IInstantiationService)),
    ( __param(5, IChatAgentService)),
    ( __param(6, IContextKeyService)),
    ( __param(7, IChatAccessibilityService)),
    ( __param(8, IChatService)),
    ( __param(9, IChatCodeBlockContextProviderService)),
    ( __param(10, IViewsService))
], TerminalChatController));

export { TerminalChatController };
